import React from 'react';
import { Plugin, Template, TemplateConnector } from '@devexpress/dx-react-core';
import { Table, VirtualTable } from '@devexpress/dx-react-grid-material-ui';
import { Highlighter } from 'app/common/table/Highlighter';
import { TableRow } from '@devexpress/dx-react-grid';
import Utils from '../../util/Utils';

interface DesktopTableRowProps<T> extends Table.DataRowProps {
    selection: Array<number | string>; // Current selection
    toggleSelection?(selection: { rowIds: Array<number | string>; state?: boolean }): void; // Function to update selection
    tableBodyRows: Array<TableRow>; // Contains the table rows, in the displayed order (when sorting is applied)
    lastSelectedRowIdx: number; // Index of the last row that has been selected
    setLastSelectedRowIdx?(idx: number): void; // Function to update the last selected row index
    highlighter?: Highlighter<T>;
    highlight?: (object: T) => boolean;
}

/**
 * The DesktopTableRow class renders a TableRow with the following features:
 *   - Desktop like row selection (click, shift+click, ctrl+click)
 *   - Highlight rows that are captured by the provided highlighter
 */
class DesktopTableRow<T> extends React.PureComponent<DesktopTableRowProps<T>> {
    handleClick = (event, value) => {
        const rowId = this.props.tableRow.rowId;
        const rowIdx = this.props.tableBodyRows.indexOf(this.props.tableRow);

        if (this.props.lastSelectedRowIdx >= 0 && event.shiftKey) {
            // Shift + click
            const fromIdx = Math.min(rowIdx, this.props.lastSelectedRowIdx);
            const toIdx = Math.max(rowIdx, this.props.lastSelectedRowIdx);
            const newSelection = new Array<number | string>();
            for (let i = fromIdx; i <= toIdx; i++) {
                newSelection.push(this.props.tableBodyRows[i].rowId);
            }
            this.props.toggleSelection({ rowIds: newSelection, state: true });
        } else if (event.ctrlKey || event.metaKey) {
            // Ctrl + click or Cmd + click for mac users
            this.props.toggleSelection({ rowIds: [rowId] });
        } else {
            // click
            this.props.toggleSelection({ rowIds: this.props.selection, state: false });
            this.props.toggleSelection({ rowIds: [rowId], state: true });
        }
        this.props.setLastSelectedRowIdx(rowIdx);
    };
    handleMouseDown = (event, value) => {
        if (event.shiftKey) {
            // prevent default browser's text selection
            event.preventDefault();
        }
    };

    render() {
        const row: T = this.props.tableRow.row;

        const cssClasses = [];
        const highlighted = this.props.highlighter?.highlight(row);
        if (highlighted) cssClasses.push(highlighted);
        if (this.props.highlight?.(row)) {
            cssClasses.push('highlight');
        }
        const selected = this.props.selection.indexOf(this.props.tableRow.rowId) !== -1;
        if (selected) cssClasses.push('selected-row');
        return (
            <VirtualTable.Row
                className={Utils.concat(' ', cssClasses)}
                onClick={this.handleClick}
                onMouseDown={this.handleMouseDown}
                row={this.props.row}
                tableRow={this.props.tableRow}>
                {this.props.children}
            </VirtualTable.Row>
        );
    }
}

interface DesktopBehaviourProps<T> {
    highlighter: Highlighter<T>;
    highlight?: (object: T) => boolean;
}

/**
 * The DesktopBehaviour plugin overrides the rendering of table row, by adding the following features:
 *   - Desktop like row selection (click, shift+click, ctrl+click)
 *   - Highlight rows that are captured by the provided highlighter
 */
export class DesktopBehaviour<T> extends React.PureComponent<DesktopBehaviourProps<T>> {
    lastSelectedRowIdx: number;
    setLastSelectedRowIdx = (idx: number) => {
        this.lastSelectedRowIdx = idx;
    };
    render() {
        // TODO: Add plugin "dependencies" attribute
        return (
            <Plugin>
                <Template
                    name="tableRow"
                    predicate={(params: Table.RowProps) => params.tableRow.type.description === 'data'}>
                    {(params: Table.DataRowProps) => (
                        <TemplateConnector>
                            {({ selection, tableBodyRows }, { toggleSelection }) => {
                                return (
                                    <DesktopTableRow
                                        {...params}
                                        tableRow={params.tableRow}
                                        selection={selection}
                                        toggleSelection={toggleSelection}
                                        tableBodyRows={tableBodyRows}
                                        lastSelectedRowIdx={this.lastSelectedRowIdx}
                                        setLastSelectedRowIdx={this.setLastSelectedRowIdx}
                                        highlighter={this.props.highlighter}
                                        highlight={this.props.highlight}
                                    />
                                );
                            }}
                        </TemplateConnector>
                    )}
                </Template>
            </Plugin>
        );
    }
}
